import { useState } from "react";
import { Plus, Trash2, Edit2, BarChart2, Megaphone } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { StatusBadge } from "@/components/admin/StatusBadge";
import { ConfirmModal } from "@/components/admin/ConfirmModal";
import { DataTable } from "@/components/admin/DataTable";
import type { DataTableColumn } from "@/components/admin/DataTable";
import { getAnnouncements } from "@/lib/admin/queries";
import { createAnnouncement, deleteAnnouncement } from "@/lib/admin/actions";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Switch } from "@/components/ui/switch";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";

const announcementSchema = z.object({
  title: z.string().min(1, "Title is required"),
  body: z.string().min(1, "Body is required"),
  audience: z.string().min(1, "Audience is required"),
  buildingId: z.string().optional(),
  userId: z.string().optional(),
  delivery: z.enum(["in_app", "email", "both"]),
  scheduled: z.boolean(),
  scheduledAt: z.string().optional(),
});

type AnnouncementForm = z.infer<typeof announcementSchema>;

const AUDIENCE_LABELS: Record<string, string> = {
  all: "All users",
  hosts: "All hosts",
  managers: "All managers",
  building: "Specific building",
  user: "Specific user",
};

const DELIVERY_LABELS: Record<string, string> = {
  in_app: "In-app only",
  email: "Email only",
  both: "Both",
};

export default function Announcements() {
  const { profile } = useAuth();
  const actorId = profile?.id ?? "";
  const qc = useQueryClient();

  const [sheetOpen, setSheetOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [statsTarget, setStatsTarget] = useState<{ id: string; title: string } | null>(null);

  const announcementsQuery = useQuery({
    queryKey: ["admin", "announcements"],
    queryFn: () => getAnnouncements(),
  });

  const announcements: Record<string, unknown>[] = announcementsQuery.data?.data ?? [];

  const form = useForm<AnnouncementForm>({
    resolver: zodResolver(announcementSchema),
    defaultValues: {
      title: "",
      body: "",
      audience: "all",
      delivery: "both",
      scheduled: false,
      scheduledAt: "",
    },
  });

  const watchAudience = form.watch("audience");
  const watchScheduled = form.watch("scheduled");

  const createMutation = useMutation({
    mutationFn: (data: AnnouncementForm) =>
      createAnnouncement(
        {
          title: data.title,
          body: data.body,
          audience: data.audience,
          delivery: data.delivery,
          scheduledAt: data.scheduled && data.scheduledAt ? data.scheduledAt : undefined,
        },
        actorId
      ),
    onSuccess: () => {
      toast.success("Announcement created");
      qc.invalidateQueries({ queryKey: ["admin", "announcements"] });
      setSheetOpen(false);
      form.reset();
    },
    onError: () => toast.error("Failed to create announcement"),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteAnnouncement(id, actorId),
    onSuccess: () => {
      toast.success("Announcement deleted");
      qc.invalidateQueries({ queryKey: ["admin", "announcements"] });
      setDeleteTarget(null);
    },
    onError: () => toast.error("Failed to delete announcement"),
  });

  const getStatus = (ann: Record<string, unknown>): string => {
    if (ann.sent_at) return "sent";
    if (ann.scheduled_at) return "scheduled";
    return "draft";
  };

  const columns: DataTableColumn<Record<string, unknown>>[] = [
    {
      key: "title",
      label: "Title",
      render: (row) => (
        <span className="font-medium text-gray-900 text-sm truncate max-w-[200px] block">
          {row.title as string}
        </span>
      ),
    },
    {
      key: "sender",
      label: "Sender",
      render: (row) => {
        const author = row.author as Record<string, unknown> | undefined;
        return (
          <span className="text-sm text-gray-600">{(author?.full_name as string) ?? "—"}</span>
        );
      },
    },
    {
      key: "audience",
      label: "Audience",
      render: (row) => (
        <Badge variant="secondary" className="text-xs whitespace-nowrap">
          {AUDIENCE_LABELS[row.audience as string] ?? (row.audience as string) ?? "—"}
        </Badge>
      ),
    },
    {
      key: "building",
      label: "Building",
      render: (row) => (
        <span className="text-sm text-gray-500 whitespace-nowrap">
          {(row.building_name as string) ?? "—"}
        </span>
      ),
    },
    {
      key: "created_at",
      label: "Sent At",
      sortable: true,
      render: (row) => (
        <span className="text-sm tabular-nums text-gray-500 whitespace-nowrap">
          {row.sent_at
            ? new Date(row.sent_at as string).toLocaleDateString("en-CA")
            : row.created_at
            ? new Date(row.created_at as string).toLocaleDateString("en-CA")
            : "—"}
        </span>
      ),
    },
    {
      key: "status",
      label: "Status",
      render: (row) => (
        <span className="whitespace-nowrap">
          <StatusBadge status={getStatus(row)} />
        </span>
      ),
    },
    {
      key: "delivery",
      label: "Delivery",
      render: (row) => (
        <span className="text-xs text-gray-500 whitespace-nowrap">
          {DELIVERY_LABELS[row.delivery as string] ?? (row.delivery as string) ?? "—"}
        </span>
      ),
    },
    {
      key: "actions",
      label: "",
      render: (row) => (
        <div className="flex items-center gap-1">
          {getStatus(row) === "draft" && (
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              aria-label="Edit announcement"
              onClick={() => {
                form.reset({
                  title: (row.title as string) ?? "",
                  body: (row.body as string) ?? "",
                  audience: (row.audience as string) ?? "all",
                  delivery: (row.delivery as "in_app" | "email" | "both") ?? "both",
                  scheduled: !!(row.scheduled_at),
                  scheduledAt: (row.scheduled_at as string) ?? "",
                });
                setSheetOpen(true);
              }}
            >
              <Edit2 className="h-3.5 w-3.5" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            aria-label="View stats"
            onClick={() => setStatsTarget({ id: row.id as string, title: row.title as string })}
          >
            <BarChart2 className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-red-500 hover:text-red-600"
            aria-label="Delete announcement"
            onClick={() => setDeleteTarget(row.id as string)}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      ),
    },
  ];

  // Find the full announcement data for the stats dialog
  const statsRow = statsTarget
    ? announcements.find((a) => (a.id as string) === statsTarget.id) ?? null
    : null;

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Announcements</h1>
        <Button
          onClick={() => {
            form.reset();
            setSheetOpen(true);
          }}
          className="gap-2 whitespace-nowrap"
        >
          <Plus className="h-4 w-4" />
          Create Announcement
        </Button>
      </div>

      <DataTable
        columns={columns}
        data={announcements}
        loading={announcementsQuery.isLoading}
        emptyMessage="No announcements yet. Create one to get started."
        onSearch={() => {}}
        searchPlaceholder="Search announcements…"
      />

      {/* Create / Edit Sheet */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <Megaphone className="h-5 w-5" />
              Create Announcement
            </SheetTitle>
          </SheetHeader>

          <Form {...form}>
            <form
              onSubmit={form.handleSubmit((data) => createMutation.mutate(data))}
              className="mt-6 space-y-5"
            >
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Title</FormLabel>
                    <FormControl>
                      <Input placeholder="Announcement title…" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="body"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Body</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Write your announcement…"
                        rows={5}
                        {...field}
                      />
                    </FormControl>
                    <p className="text-xs text-gray-400 mt-1">
                      Supports plain text and line breaks.
                    </p>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="audience"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Audience</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="all">All users</SelectItem>
                        <SelectItem value="hosts">All hosts</SelectItem>
                        <SelectItem value="managers">All managers</SelectItem>
                        <SelectItem value="building">Specific building</SelectItem>
                        <SelectItem value="user">Specific user</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {watchAudience === "building" && (
                <FormField
                  control={form.control}
                  name="buildingId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Building</FormLabel>
                      <FormControl>
                        <Input placeholder="Building name or ID…" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              {watchAudience === "user" && (
                <FormField
                  control={form.control}
                  name="userId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>User</FormLabel>
                      <FormControl>
                        <Input placeholder="User email or ID…" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              <FormField
                control={form.control}
                name="delivery"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Delivery</FormLabel>
                    <FormControl>
                      <RadioGroup
                        value={field.value}
                        onValueChange={field.onChange}
                        className="flex flex-col gap-2 mt-1"
                      >
                        {(["in_app", "email", "both"] as const).map((v) => (
                          <div key={v} className="flex items-center gap-2">
                            <RadioGroupItem value={v} id={`delivery-${v}`} />
                            <Label htmlFor={`delivery-${v}`} className="text-sm font-normal cursor-pointer">
                              {DELIVERY_LABELS[v]}
                            </Label>
                          </div>
                        ))}
                      </RadioGroup>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="scheduled"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex items-center gap-3">
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          id="schedule-toggle"
                        />
                      </FormControl>
                      <Label htmlFor="schedule-toggle" className="text-sm font-normal cursor-pointer">
                        Schedule for later
                      </Label>
                    </div>
                  </FormItem>
                )}
              />

              {watchScheduled && (
                <FormField
                  control={form.control}
                  name="scheduledAt"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Scheduled date &amp; time</FormLabel>
                      <FormControl>
                        <Input type="datetime-local" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              <div className="flex items-center gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1 whitespace-nowrap"
                  onClick={() => setSheetOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 whitespace-nowrap"
                  disabled={createMutation.isPending}
                >
                  {createMutation.isPending ? "Creating…" : "Create Announcement"}
                </Button>
              </div>
            </form>
          </Form>
        </SheetContent>
      </Sheet>

      {/* View Stats Dialog */}
      <Dialog open={!!statsTarget} onOpenChange={(v) => !v && setStatsTarget(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Announcement Stats</DialogTitle>
          </DialogHeader>
          {statsRow && (
            <div className="space-y-4 py-2">
              <p className="text-sm font-semibold text-gray-800">{statsRow.title as string}</p>
              <div className="grid grid-cols-3 gap-3">
                <div className="text-center rounded-lg bg-gray-50 border border-gray-100 p-3">
                  <p className="text-xl font-bold text-gray-900">{(statsRow.sent_count as number) ?? 0}</p>
                  <p className="text-xs text-gray-500 mt-0.5">Sent</p>
                </div>
                <div className="text-center rounded-lg bg-gray-50 border border-gray-100 p-3">
                  <p className="text-xl font-bold text-gray-900">{(statsRow.opened_count as number) ?? 0}</p>
                  <p className="text-xs text-gray-500 mt-0.5">Opened</p>
                </div>
                <div className="text-center rounded-lg bg-gray-50 border border-gray-100 p-3">
                  <p className="text-xl font-bold text-gray-900">
                    {(statsRow.sent_count as number) > 0
                      ? `${Math.round((((statsRow.opened_count as number) ?? 0) / (statsRow.sent_count as number)) * 100)}%`
                      : "—"}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">Open rate</p>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="whitespace-nowrap" onClick={() => setStatsTarget(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm */}
      <ConfirmModal
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => { if (deleteTarget) deleteMutation.mutate(deleteTarget); }}
        title="Delete Announcement"
        description="Are you sure you want to delete this announcement? This cannot be undone."
        loading={deleteMutation.isPending}
        variant="destructive"
      />
    </div>
  );
}
